import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UsernameSize } from '@freelancer/components/username';
import type {
  CategoryPageViewUser,
  ProjectViewUser,
  SearchUser,
} from '@freelancer/datastore/collections';
import { FreelancerReputation } from '@freelancer/datastore/collections';
import { ButtonColor } from '@freelancer/ui/button';
import { LinkUnderline } from '@freelancer/ui/link';
import { AvatarSize } from '@freelancer/ui/user-avatar';

@Component({
  standalone: false,
  selector: 'fl-showcase-card-action',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardHoverActionComponent {}

@Component({
  standalone: false,
  selector: 'fl-showcase-card-hover-content',
  template: `
    <fl-card
      class="ShowcaseHoverContent-userInfo"
      [level]="'secondary'"
      [edgeToEdge]="true"
      [maxContent]="false"
    >
      <div class="ShowcaseHoverContent">
        @if (user) {
          <fl-link
            class="ShowcaseHoverContent-link"
            [underline]="LinkUnderline.NEVER"
            [link]="hireLink"
          >
            <div class="ShowcaseHoverContent-userData">
              <fl-user-avatar
                class="mr-xsmall"
                [size]="AvatarSize.LARGE"
                [users]="[user]"
              ></fl-user-avatar>
              <fl-user-info
                class="ShowcaseHoverContent-userInfoContent mr-xsmall"
                [user]="user"
                [ratingControl]="ratingControl"
                [freelancerReputation]="reputation"
                [usernameSize]="UsernameSize.LARGE"
                [usernameSizeDesktop]="UsernameSize.XLARGE"
                [showCity]="showCity"
              >
              </fl-user-info>
            </div>
          </fl-link>
        } @else {
          <div class="ShowcaseHoverContent-userData">
            <div class="ShowcaseHoverContent-loading">
              <fl-loading-template
                class="ShowcaseHoverContent-loading-profilePicture mr-xsmall"
              />
              <div class="ShowcaseHoverContent-loading-text">
                <fl-loading-text
                  class="ShowcaseHoverContent-loading-text--firstLine"
                ></fl-loading-text>
                <fl-loading-text
                  class="ShowcaseHoverContent-loading-text--secondLine"
                ></fl-loading-text>
              </div>
            </div>
          </div>
        }

        @if (user) {
          <div class="ShowcaseHoverContent-actionItem">
            <ng-content select="fl-showcase-card-action"></ng-content>
            <fl-button
              i18n="Hire Freelancer Button"
              [color]="ButtonColor.PRIMARY"
              [link]="hireLink"
            >
              Hire
            </fl-button>
          </div>
        }
      </div>
    </fl-card>
    @if (cost !== undefined) {
      <fl-showcase-card-price
        class="ShowcaseHoverContent-cost"
        [cost]="cost"
        [label]="label"
      ></fl-showcase-card-price>
    }
  `,
  styleUrls: ['./showcase-card-hover-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardHoverContentComponent implements OnChanges {
  AvatarSize = AvatarSize;
  ButtonColor = ButtonColor;
  LinkUnderline = LinkUnderline;
  UsernameSize = UsernameSize;
  hireLink: string;

  @Input() user?: ProjectViewUser | SearchUser | CategoryPageViewUser;
  @Input() ratingControl = new FormControl(0, { nonNullable: true });
  @Input() reputation?: FreelancerReputation;
  @Input() cost?: number;
  @Input() label?: string;
  @Input() showCity? = true;

  ngOnChanges(changes: SimpleChanges): void {
    if ('user' in changes && this.user) {
      this.hireLink = `/u/${this.user.username}`;
    }
  }
}
